import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTenant } from './TenantContext';

interface LineOfBizContextValue {
  activeLineOfBizId: number | null;
  setActiveLineOfBizId: (id: number | null) => void;
}

const LineOfBizContext = createContext<LineOfBizContextValue | undefined>(undefined);

export function LineOfBizProvider({ children }: { children: ReactNode }) {
  const [activeLineOfBizId, setActiveLineOfBizId] = useState<number | null>(null);

  // LOBs are tenant-scoped, so a stored id from tenant A is meaningless
  // under tenant B. Reset whenever the active customer changes.
  const { activeTenantId } = useTenant();
  useEffect(() => {
    setActiveLineOfBizId(null);
  }, [activeTenantId]);

  const setter = useCallback((id: number | null) => setActiveLineOfBizId(id), []);
  const value = useMemo(
    () => ({ activeLineOfBizId, setActiveLineOfBizId: setter }),
    [activeLineOfBizId, setter],
  );
  return <LineOfBizContext.Provider value={value}>{children}</LineOfBizContext.Provider>;
}

export function useLineOfBiz(): LineOfBizContextValue {
  const ctx = useContext(LineOfBizContext);
  if (!ctx) throw new Error('useLineOfBiz must be used inside LineOfBizProvider');
  return ctx;
}
